import React from 'react';
import { Check, ArrowUpRight, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';

export interface PricingTier {
  name: string;
  price: string;
  period?: string;
  description: string;
  features: string[];
  popular?: boolean;
  ctaLabel: string;
  ctaHref?: string;
}

interface ServicePricingProps {
  eyebrow: string;
  heading: string;
  subtitle?: string;
  tiers: PricingTier[];
  note?: string;
}

const ServicePricing: React.FC<ServicePricingProps> = ({
  eyebrow,
  heading,
  subtitle,
  tiers,
  note,
}) => (
  <section className="relative py-20 md:py-28 bg-background" aria-labelledby="pricing-heading">
    <div className="pointer-events-none absolute inset-0 dot-grid opacity-[0.3]" aria-hidden="true" />

    <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-12 gap-6 mb-14 md:mb-16">
        <div className="col-span-12 md:col-span-7">
          <span className="eyebrow">— {eyebrow}</span>
          <h2
            id="pricing-heading"
            className="mt-4 font-display text-foreground text-[clamp(2rem,4vw,3.25rem)] leading-[1.05] tracking-tight"
          >
            {heading}
          </h2>
        </div>
        {subtitle && (
          <p className="col-span-12 md:col-span-4 md:col-start-9 self-end text-muted-foreground text-base md:text-lg leading-relaxed">
            {subtitle}
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
        {tiers.map((tier, idx) => (
          <article
            key={tier.name}
            className={[
              'group relative flex flex-col rounded-2xl border p-6 md:p-8 transition-all duration-500 hover:-translate-y-1',
              tier.popular
                ? 'border-lime/40 bg-card shimmer-border md:scale-[1.02] z-10 mt-4 md:mt-0'
                : 'border-border/80 bg-card/60 hover:bg-card hover:border-foreground/30',
            ].join(' ')}
          >
            {tier.popular && (
              <span className="pill-tag-lime absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                <Sparkles className="h-3 w-3" />
                Popular
              </span>
            )}

            <header className="mb-6 pb-6 border-b border-border/60">
              <div className="flex items-center justify-between mb-2">
                <span
                  className={[
                    'font-mono-tag text-xs uppercase tracking-[0.32em]',
                    tier.popular ? 'text-lime' : 'text-muted-foreground',
                  ].join(' ')}
                >
                  / 0{idx + 1}
                </span>
                <h3 className="font-display text-lg text-foreground">{tier.name}</h3>
              </div>
              <div className="flex items-baseline gap-2 mt-4">
                <span className="font-display text-5xl md:text-6xl text-foreground leading-none tracking-tight">
                  {tier.price}
                </span>
                {tier.period && (
                  <span className="font-mono-tag text-xs uppercase tracking-[0.24em] text-muted-foreground">
                    {tier.period}
                  </span>
                )}
              </div>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                {tier.description}
              </p>
            </header>

            <ul className="flex-1 space-y-3 mb-8">
              {tier.features.map((feature, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-foreground/85 leading-relaxed">
                  <Check
                    className={[
                      'h-4 w-4 mt-0.5 flex-shrink-0',
                      tier.popular ? 'text-lime' : 'text-primary',
                    ].join(' ')}
                    strokeWidth={2.5}
                  />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>

            <Link
              to={tier.ctaHref ?? '/contact'}
              className={[
                'group/btn inline-flex items-center justify-between gap-2 rounded-full px-5 py-3.5 text-sm font-medium transition-all duration-300',
                tier.popular
                  ? 'bg-lime text-lime-foreground hover:bg-lime/90 shadow-glow-lime'
                  : 'bg-card border border-border text-foreground hover:bg-secondary hover:border-foreground/30',
              ].join(' ')}
            >
              {tier.ctaLabel}
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover/btn:-translate-y-0.5 group-hover/btn:translate-x-0.5" />
            </Link>
          </article>
        ))}
      </div>

      {note && (
        <p className="mt-10 text-center text-sm text-muted-foreground max-w-2xl mx-auto">
          {note}
        </p>
      )}
    </div>
  </section>
);

export default ServicePricing;
